import React, { useEffect, useState } from 'react';
import { getUsers, blockUser, unblockUser, changeRole, assignClient, getClients } from '../utils/api';
import { PageTitle, Card, Table, Btn, Loading, Empty, Grid, StatCard, statusBadge } from '../components/UI';

export default function Users() {
  const [data, setData]       = useState({ stats: {}, users: [] });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(null);
  const [filter, setFilter]   = useState('');

  const load = () => Promise.all([
    getUsers(),
    getClients(),
  ]).then(([u, c]) => {
    setData(u.data?.data || {});
    setClients(c.data?.data?.clients || []);
  }).catch(console.error).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleBlock = async (uid, name) => {
    const reason = window.prompt(`"${name}" কে block করার কারণ:`);
    if (reason === null) return;
    setActing(uid);
    await blockUser(uid, reason).catch(console.error);
    await load();
    setActing(null);
  };

  const handleUnblock = async (uid) => {
    setActing(uid);
    await unblockUser(uid).catch(console.error);
    await load();
    setActing(null);
  };

  const handleRoleChange = async (uid, currentRole) => {
    const newRole = currentRole === 'admin' ? 'client' : 'admin';
    if (!window.confirm(`Role change করবো: ${currentRole} → ${newRole}?`)) return;
    setActing(uid);
    await changeRole(uid, newRole).catch(console.error);
    await load();
    setActing(null);
  };

  const handleAssignClient = async (uid) => {
    const clientId = window.prompt('Client ID দিন (clients page থেকে):');
    if (!clientId) return;
    setActing(uid);
    await assignClient(uid, clientId).catch(console.error);
    await load();
    setActing(null);
  };

  const s = data.stats || {};
  const filtered = (data.users || []).filter(u =>
    !filter ||
    u.name?.toLowerCase().includes(filter.toLowerCase()) ||
    u.email?.toLowerCase().includes(filter.toLowerCase()) ||
    u.role === filter || u.status === filter
  );

  return (
    <div>
      <PageTitle title="👤 Users" sub="সব registered users — block, unblock, role manage করো" />

      <Grid cols={4}>
        <StatCard label="Total Users" value={s.total || 0} color="#a29bfe" />
        <StatCard label="Clients" value={s.client || 0} color="#74b9ff" />
        <StatCard label="Admins" value={s.admin || 0} color="#ffd200" />
        <StatCard label="Blocked" value={s.blocked || 0} color="#ff6584" />
      </Grid>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['', 'client', 'admin', 'active', 'blocked'].map(f => (
          <Btn key={f} color={filter === f ? '#6c63ff' : '#1e1e2e'}
            onClick={() => setFilter(f)} style={{ fontSize: 12, padding: '7px 14px' }}>
            {f || 'All'}
          </Btn>
        ))}
      </div>

      <Card>
        {loading ? <Loading /> : filtered.length === 0 ? <Empty msg="কোনো user নেই" /> : (
          <Table
            headers={['User', 'Role', 'Status', 'Client Linked', 'Last Login', 'Actions']}
            rows={filtered.map((u, i) => (
              <tr key={i}>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e2e' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: '#6b6b80', marginTop: 2 }}>{u.email}</div>
                  {u.phone && <div style={{ fontSize: 11, color: '#6b6b80' }}>{u.phone}</div>}
                </td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e2e' }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700,
                    color: u.role === 'admin' ? '#ffd200' : '#74b9ff',
                    background: u.role === 'admin' ? 'rgba(255,210,0,0.1)' : 'rgba(116,185,255,0.1)'
                  }}>{u.role}</span>
                </td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e2e' }}>
                  {statusBadge(u.status)}
                  {u.blockedReason && (
                    <div style={{ fontSize: 11, color: '#ff6584', marginTop: 4 }}>{u.blockedReason}</div>
                  )}
                </td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e2e', fontSize: 12, color: '#6b6b80' }}>
                  {u.clientId
                    ? <span style={{ color: '#55efc4' }}>✅ {clients.find(c => c.id === u.clientId)?.name || u.clientId}</span>
                    : <span style={{ color: '#6b6b80' }}>— না</span>}
                </td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e2e', fontSize: 11, color: '#6b6b80' }}>
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('bn-BD') : 'কখনো না'}
                </td>
                <td style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e2e' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {u.status === 'blocked'
                      ? <Btn color="#55efc4" disabled={acting === u.uid} onClick={() => handleUnblock(u.uid)}
                          style={{ fontSize: 11, padding: '5px 10px' }}>✅ Unblock</Btn>
                      : <Btn color="#ff6584" disabled={acting === u.uid} onClick={() => handleBlock(u.uid, u.name)}
                          style={{ fontSize: 11, padding: '5px 10px' }}>⛔ Block</Btn>
                    }
                    <Btn color="#ffd200" disabled={acting === u.uid} onClick={() => handleRoleChange(u.uid, u.role)}
                      style={{ fontSize: 11, padding: '5px 10px', color: '#0f0f17' }}>
                      👑 Role
                    </Btn>
                    {u.role === 'client' && !u.clientId && (
                      <Btn color="#a29bfe" disabled={acting === u.uid} onClick={() => handleAssignClient(u.uid)}
                        style={{ fontSize: 11, padding: '5px 10px' }}>
                        🔗 Assign
                      </Btn>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          />
        )}
      </Card>
    </div>
  );
}
